"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.getArrowSize = getArrowSize;
exports.getFlyoutAttributes = getFlyoutAttributes;
exports.getFlyoutStyle = getFlyoutStyle;
exports.isEventOutside = isEventOutside;
exports.outsideListeners = outsideListeners;
exports.overflowDetectionMiddleware = void 0;
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _reactDom = require("@floating-ui/react-dom");
const _excluded = ["x", "y", "strategy", "arrow"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { (0, _defineProperty2.default)(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * Determine the arrow size given the input
 *
 * @param {Mixed} arrow - Value passed in from the props
 * @returns {String} - Final value to assign to the arrow size
 */
function getArrowSize(arrow) {
  switch (typeof arrow) {
    case 'boolean':
      return arrow ? '1rem' : 0;
    case 'number':
      return `${arrow}px`;
    default:
      return arrow;
  }
}

/**
 * Determine if the event occurred outside of the targets' contexts
 *
 * @param {Object} ev - Original event object
 * @param {Array<Element>} targets - Elements to check against
 * @returns {Boolean} - Event occurred outside
 */
function isEventOutside(ev, targets) {
  const includedTargets = ev.composedPath().filter(elem => targets.includes(elem));
  return !includedTargets.length;
}

/**
 * Creates data-attributes for flyout metadata
 *
 * @param {Object} config - Configuration object
 * @param {Object} config.hide - Values returned from the hide() middleware
 * @param {String} config.placement - Value describing the placement set as a result of the @floating-ui library
 * @returns {Object} - Attributes to spread onto the flyout component
 */
function getFlyoutAttributes(_ref) {
  let {
    hide,
    placement
  } = _ref;
  const {
    escaped,
    referenceHidden
  } = hide || {};
  return {
    'data-placement': placement,
    'data-escaped': escaped,
    'data-refhidden': referenceHidden
  };
}

/**
 * Prepares the styles to apply to the flyout
 *
 * @param {Object} config - Configuration object
 * @param {Number} config.x - Horizontal position
 * @param {Number} config.y - Vertical position
 * @param {String} config.strategy - Positioning strategy
 * @param {String} config.arrow - Arrow styles
 * @returns {Object} - Styles to apply to the flyout
 */
function getFlyoutStyle(_ref2) {
  let {
      x: left,
      y: top,
      strategy: position,
      arrow
    } = _ref2,
    styles = (0, _objectWithoutProperties2.default)(_ref2, _excluded);
  // NOTE: Width with "max-content" is needed to get correct @floating-ui size/placement when
  //   the trigger element is close to the right edge. e.g. A Modal with a Flyout anchor button
  //   that is next to the edge of the modal box. Max width is needed because "max-content" can
  //   cause Flyout to be wider than the viewport when in mobile device or very narrow screens.
  //
  //   Ref: https://floating-ui.com/docs/computeposition#initial-layout
  //
  const baseStyles = _objectSpread({
    position,
    top,
    left,
    width: 'max-content'
  }, styles);
  if (arrow) {
    return Object.assign(baseStyles, {
      '--ux-flyout-arrow-size': arrow.size || 0,
      '--ux-flyout-arrow-top': `${arrow.top}px` || 0,
      '--ux-flyout-arrow-left': `${arrow.left}px` || 0
    });
  }
  return baseStyles;
}

/**
 * Setup for the useEffect for the document listeners
 *
 * @param {Array<Element>} targets - Elements to check against
 * @param {Function} callback - Function to execute when an event occurs outside
 * @returns {Function} - Teardown function
 */
function outsideListeners(targets, callback) {
  function outsideEvent(ev) {
    isEventOutside(ev, targets) && callback(ev);
  }
  function escKey(ev) {
    ev.key === 'Escape' && callback(ev);
  }
  if (typeof callback === 'function') {
    document.addEventListener('pointerdown', outsideEvent);
    document.addEventListener('keydown', escKey);
  }
  return () => {
    document.removeEventListener('pointerdown', outsideEvent);
    document.removeEventListener('keydown', escKey);
  };
}

/**
 * Middleware to detect overflow and adjust the position of the flyout
 * to prevent it from increasing the width of the document (causing infinite scroll).
 *
 * @returns {Object} - Middleware data object for use with Floating UI
 */
const overflowDetectionMiddleware = exports.overflowDetectionMiddleware = {
  name: 'overflowDetection',
  async fn(state) {
    const overflow = await (0, _reactDom.detectOverflow)(state, {
      altBoundary: true,
      rootBoundary: 'document'
    });

    // If there's overflow to the right, adjust the position
    if (overflow.right > 0) {
      // Calculate how much we need to move left to fit
      const adjustment = Math.min(overflow.right, state.x);

      // Return a fixed position that won't cause scrolling
      return {
        x: state.x - adjustment,
        middlewareData: _objectSpread(_objectSpread({}, state.middlewareData), {}, {
          overflowDetection: {
            overflow,
            adjusted: true
          }
        })
      };
    }

    // If no overflow, return the state unchanged
    return {
      middlewareData: _objectSpread(_objectSpread({}, state.middlewareData), {}, {
        overflowDetection: {
          overflow,
          adjusted: false
        }
      })
    };
  }
};